import type { UserEntry } from '@common/types/users';
import { useQuery } from '@tanstack/react-query';
import { fetchAllUsers, fetchUserById } from '@/services/UserService';
import useIdentityStore from '@/stores/useIdentityStore';

export function useUser(selectedUserId: number | null) {
    return useQuery({
        queryKey: ['user', selectedUserId],
        queryFn: async () => {
            if (!selectedUserId) return null;
            const { data } = await fetchUserById(selectedUserId);
            return data;
        },
        enabled: !!selectedUserId,
        staleTime: 5 * 60_000,
    });
}

interface Params {
    lawFirmId?: number | null;
    enabled?: boolean;
}

export function useUsers({ lawFirmId, enabled = true }: Params = {}) {
    const loggedInUser = useIdentityStore((state) => state.user);
    const myLawFirmId = useIdentityStore((state) => state.myLawFirmId);

    // Only pass an explicit firm to the API when it differs from the user's
    // own firm; the API scopes to the caller's firm by default.
    const requestLawFirmId = lawFirmId != null && lawFirmId !== myLawFirmId ? lawFirmId : undefined;
    const queryLawFirmId = lawFirmId ?? myLawFirmId;

    return useQuery<UserEntry[]>({
        queryKey: ['users', { lawFirmId: queryLawFirmId }],
        queryFn: async () => {
            const response = await fetchAllUsers(requestLawFirmId);
            return response.data;
        },
        enabled: enabled && !!loggedInUser,
        staleTime: 5 * 60_000,
    });
}
